import { useState } from "react";
import { Phone, Mail, MessageCircle, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useSEO, useSettings } from "@/lib/hooks";

export default function ContactPage() {
  const s = useSettings();
  useSEO({ title: "Contact — Depann7", description: "Contactez Depann7 par téléphone, WhatsApp ou email. Disponible 24h/24 et 7j/7 en Île-de-France." });
  const [d, setD] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const setF = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", d);
      toast.success("Message envoyé. Nous vous répondons rapidement.");
      setD({ name: "", email: "", phone: "", message: "" });
    } catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };

  const inputCls = "w-full h-12 px-4 bg-surface-50 border border-surface-200 rounded-xl text-txt-900 text-sm placeholder:text-txt-300 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all";

  return (
    <div className="bg-white">
      <div className="bg-ink-900 text-white py-14">
        <div className="max-w-6xl mx-auto px-4 lg:px-6">
          <h1 className="text-3xl lg:text-4xl font-extrabold">Contact</h1>
          <p className="text-ink-300 mt-2">Plusieurs moyens de nous joindre, 24h/24.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-12 grid lg:grid-cols-2 gap-8">
        <div className="space-y-3">
          <ContactCard Icon={Phone} title="Téléphone" value={s?.company_phone || "07 53 95 58 90"} href={`tel:${s?.company_phone_raw || ""}`} />
          <ContactCard Icon={MessageCircle} title="WhatsApp" value="Réponse rapide" href={`https://wa.me/${s?.company_whatsapp || ""}`} />
          <ContactCard Icon={Mail} title="Email" value={s?.company_email || "Contact.depannidf@gmail.com"} href={`mailto:${s?.company_email || ""}`} />
          <ContactCard Icon={Clock} title="Horaires" value="24h/24 — 7j/7" />
          <ContactCard Icon={MapPin} title="Zone d'intervention" value="Île-de-France complète" />
          <iframe
            title="Île-de-France"
            src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d167936.5!2d2.343!3d48.866!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sfr!2sfr!4v1700000000000"
            className="w-full h-64 rounded-2xl mt-4"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <form onSubmit={submit} className="bg-white border border-surface-200 rounded-2xl p-6 sm:p-8 shadow-card" data-testid="contact-form">
          <h2 className="text-xl font-extrabold text-txt-900">Envoyer un message</h2>
          <p className="text-txt-500 text-sm mt-1">Nous vous répondons dans les plus brefs délais</p>
          <div className="grid gap-3 mt-6">
            <input className={inputCls} placeholder="Nom *" value={d.name} onChange={setF("name")} data-testid="cf-name" required />
            <input className={inputCls} placeholder="Email *" type="email" value={d.email} onChange={setF("email")} data-testid="cf-email" required />
            <input className={inputCls} placeholder="Téléphone" value={d.phone} onChange={setF("phone")} data-testid="cf-phone" />
            <textarea className={`${inputCls} min-h-[140px] h-auto py-3 resize-y`} placeholder="Votre message *" value={d.message} onChange={setF("message")} data-testid="cf-message" required />
          </div>
          <button disabled={loading} className="mt-5 w-full bg-brand hover:bg-brand-600 text-white font-bold rounded-xl py-4 disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="cf-submit">
            {loading ? "Envoi..." : "Envoyer"} <Send size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function ContactCard({ Icon, title, value, href }) {
  const Wrap = href ? "a" : "div";
  return (
    <Wrap href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="bg-white border border-surface-200 hover:border-brand rounded-2xl p-4 flex items-center gap-4 transition-colors">
      <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
        <Icon className="text-brand" size={20} />
      </div>
      <div>
        <div className="text-xs text-txt-500 font-medium">{title}</div>
        <div className="font-bold text-txt-900">{value}</div>
      </div>
    </Wrap>
  );
}
